import { tokenManager } from '../../js/utils/TokenManager.js';
import { USER_WORKER_URL } from '../../js/config.js';
import { parseMembershipUsageResponse } from '../../js/utils/membershipUsage.js';
import { storage } from '../../js/utils/storage.js';
import { getCachedOpenRouterKeyConfigured } from '../../js/utils/openRouterKeyStatus.js';

export const MAX_ENERGY = 10;
const ENERGY_STORAGE_KEY = 'user_energy_state';
const SECONDS_PER_HOUR = 3600;

const energyUiUpdaters = new Set();
let energyInterval = null;
let membershipSyncInFlight = null;
let stopRuntimeWhenIdle = null;

const getSecondsUntilNextUtcHour = () => {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const nextHourBoundary = Math.ceil(nowSeconds / SECONDS_PER_HOUR) * SECONDS_PER_HOUR;
  return Math.max(0, nextHourBoundary - nowSeconds);
};

const getEnergyState = () => {
  const saved = storage.get(ENERGY_STORAGE_KEY);
  if (saved && typeof saved === 'object') return saved;
  return null;
};

const hasValidCachedEnergyState = () => {
  const state = getEnergyState();
  return Boolean(state && Number.isFinite(state.count));
};

const persistEnergyState = (state) => {
  storage.set(ENERGY_STORAGE_KEY, state);
};

const getRemainingRefillSeconds = (state) => {
  const synced = Number(state?.secondsUntilNext);
  const ts = Number(state?.syncTimestamp);
  if (Number.isFinite(synced) && Number.isFinite(ts)) {
    return Math.max(0, synced - Math.floor((Date.now() - ts) / 1000));
  }
  return getSecondsUntilNextUtcHour();
};

const formatMinutesSeconds = (remainingSeconds) => {
  const mins = Math.floor(remainingSeconds / 60);
  const secs = remainingSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const notifyEnergyUIs = () => {
  energyUiUpdaters.forEach((updateUI) => updateUI());
};

const applyHourlyRefillIfDue = () => {
  const state = getEnergyState();
  if (!state || !Number.isFinite(state.count)) return;
  if (getRemainingRefillSeconds(state) > 0) return;
  if (state.count >= MAX_ENERGY) return;

  state.count = Math.min(MAX_ENERGY, state.count + 1);
  state.secondsUntilNext = SECONDS_PER_HOUR;
  state.syncTimestamp = Date.now();
  persistEnergyState(state);
};

const renderEnergyState = ({ state, countText, timerText, icon }) => {
  countText.textContent = `${state.count}`;

  if (state.count > MAX_ENERGY) {
    timerText.textContent = '';
    icon.style.color = 'var(--color-warning)';
    return;
  }

  if (state.count === MAX_ENERGY) {
    timerText.textContent = 'MAX';
    icon.style.color = 'var(--color-warning)';
    return;
  }

  timerText.textContent = formatMinutesSeconds(getRemainingRefillSeconds(state));
  icon.style.color = state.count === 0
    ? 'var(--color-border-light)'
    : 'var(--color-warning)';
};

const createEnergyUpdateUI = ({ countText, timerText, icon }) => () => {
  const state = getEnergyState();
  if (!state) return;
  renderEnergyState({ state, countText, timerText, icon });
};

/**
 * @param {{ onSync?: (state: { count: number, secondsUntilNext: number, syncTimestamp: number }) => void, force?: boolean }} options
 */
const syncEnergyWithBackend = async ({ onSync, force = false } = {}) => {
  if (!force && hasValidCachedEnergyState()) return;
  if (membershipSyncInFlight) {
    await membershipSyncInFlight;
    onSync?.(getEnergyState());
    return;
  }

  membershipSyncInFlight = (async () => {
    try {
      const token = await tokenManager.getValidAccessToken();
      if (!token) return;

      const response = await fetch(`${USER_WORKER_URL}/membership-usage`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) return;

      const result = await response.json();
      const usageData = parseMembershipUsageResponse(result);
      if (!usageData) return;

      const state = {
        count: usageData.buzz,
        secondsUntilNext: usageData.seconds_until_next_buzz || SECONDS_PER_HOUR,
        syncTimestamp: Date.now()
      };
      persistEnergyState(state);
      onSync?.(state);
    } catch (e) {
      console.warn('[Energy] Failed to sync with backend:', e);
    } finally {
      membershipSyncInFlight = null;
    }
  })();

  await membershipSyncInFlight;
};

export const shouldShowEnergyBar = (userData) => {
  if (!userData?.isLoggedIn) return false;
  const currentTier = userData?.current_tier || 'free';
  if (currentTier !== 'free') return false;
  return getCachedOpenRouterKeyConfigured() !== true;
};

export const applyEnergyFromServer = ({ count } = {}) => {
  if (!Number.isFinite(count)) return;
  const previous = getEnergyState() || {};
  persistEnergyState({
    ...previous,
    count
  });
  notifyEnergyUIs();
};

const ensureEnergyRuntime = () => {
  if (energyInterval) return;
  energyInterval = setInterval(() => {
    applyHourlyRefillIfDue();
    notifyEnergyUIs();
  }, 1000);
  syncEnergyWithBackend({ onSync: notifyEnergyUIs });
};

const subscribeEnergyUI = (updateUI) => {
  if (stopRuntimeWhenIdle) {
    clearTimeout(stopRuntimeWhenIdle);
    stopRuntimeWhenIdle = null;
  }
  energyUiUpdaters.add(updateUI);
  ensureEnergyRuntime();
  updateUI();

  return () => {
    energyUiUpdaters.delete(updateUI);
    if (energyUiUpdaters.size > 0) return;
    stopRuntimeWhenIdle = setTimeout(() => {
      stopRuntimeWhenIdle = null;
      if (energyUiUpdaters.size > 0) return;
      if (!energyInterval) return;
      clearInterval(energyInterval);
      energyInterval = null;
    }, 0);
  };
};

const createEnergyView = ({
  containerClass,
  iconClass,
  countClass,
  timerClass,
  refreshOnHover = false
}) => {
  const container = document.createElement('div');
  container.className = containerClass;

  const icon = document.createElement('i');
  icon.className = iconClass;

  const countText = document.createElement('span');
  countText.className = countClass;
  countText.textContent = '...';

  const timerText = document.createElement('span');
  timerText.className = timerClass;
  timerText.textContent = '';

  const updateUI = createEnergyUpdateUI({ countText, timerText, icon });
  const unsubscribe = subscribeEnergyUI(updateUI);

  if (refreshOnHover) container.addEventListener('mouseenter', updateUI);

  container.appendChild(countText);
  container.appendChild(icon);
  container.appendChild(timerText);

  return {
    element: container,
    cleanup: unsubscribe
  };
};

/**
 * @returns {{ element: HTMLElement, cleanup: () => void }}
 */
export function createSideNavEnergy() {
  return createEnergyView({
    containerClass: 'side-nav-energy-container energy-bar-container',
    iconClass: 'fas fa-bolt side-nav-energy-icon',
    countClass: 'energy-count',
    timerClass: 'side-nav-energy-timer'
  });
}

/**
 * @returns {{ element: HTMLElement, cleanup: () => void }}
 */
export function createEnergyBar() {
  return createEnergyView({
    containerClass: 'nav-energy-bar',
    iconClass: 'fas fa-bolt nav-energy-bar__icon',
    countClass: 'nav-energy-bar__count',
    timerClass: 'nav-energy-bar__timer',
    refreshOnHover: true
  });
}
